import React, { useState, ReactNode, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import chevronIc from '@/assets/icons/managementArea/chevron.svg';
import chevronActiveIc from '@/assets/icons/managementArea/chevron-active.svg';
import { ManagementAreaSidebarItem } from './managementAreaSidebarData';
import styles from './ManagementAreaSidebar.module.css';
import useManagementBadges from './useManagementBadges';
import { useAnnouncements } from '@/components/Announcements/AnnouncementsContext';
import NavBadge from '@/components/sidebar/NavBadge';

interface ManagementAreaNavItemProps {
  to?: string;
  children?: ReactNode;
  sideBarItem: ManagementAreaSidebarItem;
  isCollapsed?: boolean;
  /** 0 = top-level item, 1 = child of an expandable group */
  depth?: number;
  /** The sidebar is an icon rail: labels are hidden and sub-menus have nowhere to open. */
  isRail?: boolean;
  /** Called when a rail item needs the sidebar back at full width to be usable. */
  onRequestExpand?: () => void;
  /**
   * Match the route exactly instead of by prefix. NavLink's default lights an item up on
   * every route that starts with its own, so a parent path stays "active" while one of its
   * sub-paths is the screen actually open. The sidebar sets this where that would mislead.
   */
  end?: boolean;
}

const enabledRoutes: string[] = import.meta.env.VITE_APP_ENABLED_ROUTES;

// Same per-site gating as the main sidebar's NavItem: items whose route is not
// in VITE_APP_ENABLED_ROUTES render blurred and non-clickable.
const isNavItemEnabled = (navItem: ManagementAreaSidebarItem): boolean => {
  if (navItem.children) return navItem.children.some((child) => isNavItemEnabled(child));
  // External links live outside the app's routing, so VITE_APP_ENABLED_ROUTES
  // has nothing to say about them; their own config decides whether they exist.
  if (navItem.href) return true;
  // A feature-flagged item is controlled solely by its flag, as in the main sidebar's NavItem.
  // ManagementAreaSidebar already drops it while the flag is off, so reaching here means it is on.
  if (navItem.featureKey) return true;
  return navItem.to ? enabledRoutes.includes(navItem.to) : false;
};

const ManagementAreaNavItem: React.FC<ManagementAreaNavItemProps> = ({
  to,
  children,
  sideBarItem,
  isCollapsed = false,
  depth = 0,
  isRail = false,
  onRequestExpand,
  end = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isExpandable = Boolean(children);
  const location = useLocation();
  const [hasActiveChild, setHasActiveChild] = useState(false);
  const autoExpandedRef = useRef(false);
  const badges = useManagementBadges();
  const badgeCount = sideBarItem.badgeKey ? (badges[sideBarItem.badgeKey] || 0) : 0;
  const isRouteEnabled = isNavItemEnabled(sideBarItem);
  const isChild = depth > 0;

  /*
   * «חדש» while a new management screen's announcement is unread (HAT-1489), like the fund menu.
   * ManagementAreaSidebar already drops an item whose flag is closed, so every key here is open.
   */
  const announcements = useAnnouncements();
  const featureKeysOf = (item: ManagementAreaSidebarItem): (string | undefined)[] => [
    item.featureKey,
    ...(item.children ?? []).flatMap(featureKeysOf),
  ];
  const isNew = !!announcements?.hasUnread(featureKeysOf(sideBarItem), 'management');

  useEffect(() => {
    if (sideBarItem.children) {
      const isAnyChildActive = sideBarItem.children.some((child) => {
        return child.to ? location.pathname.startsWith(child.to) : false;
      });

      setHasActiveChild(isAnyChildActive);

      if (isAnyChildActive && !isCollapsed && !autoExpandedRef.current) {
        setIsExpanded(true);
        autoExpandedRef.current = true;
      }

      if (!isAnyChildActive) {
        autoExpandedRef.current = false;
      }
    }
  }, [sideBarItem.children, location.pathname, isCollapsed]);

  const handleExpandClick = () => {
    if (!isExpandable) return;
    // A sub-menu has nowhere to unfold inside a 76px rail, so opening one is
    // also a request to open the menu.
    if (isRail) {
      onRequestExpand?.();
      setIsExpanded(true);
    } else {
      setIsExpanded(!isExpanded);
    }
    autoExpandedRef.current = true;
  };

  // Without a label beside it an icon has to name itself somehow.
  const railTitle = isRail ? t(sideBarItem.text) : undefined;

  // An open group shows its children, and the child carries the count - once is enough
  const showsBadge = badgeCount > 0 && !(isExpandable && isExpanded && !isCollapsed && !isRail);
  const countBadge = showsBadge ? (
    <NavBadge
      count={badgeCount}
      className={styles.badge}
      iconicClassName={styles.badgeIconic}
      icon={sideBarItem.badgeIcon}
      tooltipKey={sideBarItem.badgeTooltip}
      // A closed group only says that something waits inside; the item says what and how many
      asDot={isExpandable}
      dotClassName={styles.badgeDot}
    />
  ) : null;
  const badgeElement = (
    <>
      {countBadge}
      {isNew && <span className={styles.newChip}>{t('announcements.new')}</span>}
    </>
  );

  const getContent = (isActive: boolean) => (
    <>
      {sideBarItem.iconSrc && (
        <span className={styles.iconHolder}>
          <img
            src={isActive && sideBarItem.activeIconSrc ? sideBarItem.activeIconSrc : sideBarItem.iconSrc}
            alt=""
            aria-hidden="true"
          />
        </span>
      )}
      <span className={styles.textGroup}>
        <span className={styles.label}>{t(sideBarItem.text)}</span>
        {badgeElement}
        {isExpandable && (
          <img
            src={isActive ? chevronActiveIc : chevronIc}
            alt=""
            className={isExpanded ? styles.expanderIconExpanded : styles.expanderIcon}
            aria-hidden="true"
          />
        )}
      </span>
    </>
  );

  if (isExpandable) {
    const isActiveLook = hasActiveChild && (isCollapsed || !isExpanded);
    return (
      <>
        <button
          className={`${styles.navItem} ${isActiveLook ? styles.activeNavItem : ''} ${!isRouteEnabled ? styles.disabledNavItem : ''}`}
          onClick={handleExpandClick}
          aria-expanded={isExpanded}
          tabIndex={isRouteEnabled ? 0 : -1}
          title={railTitle}
        >
          {getContent(isActiveLook)}
        </button>

        {!isCollapsed && (
          <div className={`${styles.childrenContainer} ${isExpanded ? styles.childrenContainerExpanded : ''}`}>
            <div className={styles.childrenInner}>{children}</div>
          </div>
        )}
      </>
    );
  }

  if (sideBarItem.href) {
    return (
      <a
        href={sideBarItem.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.navItem} ${styles.externalLink} ${isChild ? styles.childNavItem : ''}`}
      >
        {getContent(false)}
        <OpenInNewIcon fontSize="inherit" className={styles.externalIcon} aria-hidden="true" />
      </a>
    );
  }

  return (
    <NavLink
      to={to || ''}
      end={end}
      className={({ isActive }) =>
        [
          styles.navItem,
          isChild ? styles.childNavItem : '',
          isActive ? (isChild ? styles.activeChildNavItem : styles.activeNavItem) : '',
          !isRouteEnabled ? styles.disabledNavItem : '',
        ].join(' ')
      }
      tabIndex={isRouteEnabled ? 0 : -1}
      title={railTitle}
    >
      {({ isActive }) => getContent(isActive && !isChild)}
    </NavLink>
  );
};

export default ManagementAreaNavItem;
